import React from 'react'
import PropTypes from 'prop-types'
import { getWidgetFilters, updateWidgetFilters } from './filter-requests'
import ChipInput from './chip-input'
import './filter-list-management.css'

const TOOLTIP =
  'Press Enter to add each word as a tag. Words can include spaces or commas ' +
  "— they're only added on Enter. Pasting a list adds several at once (see " +
  '"Paste splits on"). These look like tags but are simply the words being ' +
  'filtered.'

const FIELDS = [
  {
    key: 'words',
    title: 'Word filters',
    subTitle: 'Exclude Tweets which contain certain words'
  },
  {
    key: 'handles',
    title: 'Handle filters',
    subTitle: 'Exclude Tweets from certain @handles'
  },
  {
    key: 'allowWords',
    title: 'Allow list',
    subTitle: 'Always allow Tweets containing these words'
  }
]

const SPLIT_MODES = [
  { value: 'lines', label: 'Lines' },
  { value: 'commas', label: 'Commas' },
  { value: 'none', label: 'None' }
]

// Normalise a list loaded from the API: keep strings only, trim, drop empties.
const toArray = value =>
  (Array.isArray(value) ? value : [])
    .filter(item => typeof item === 'string')
    .map(item => item.trim())
    .filter(Boolean)

/**
 * Per-widget moderation filter editor. Reads and writes the live
 * `widget_data.blacklistConfig.{words,handles,allowWords}` store via the
 * `/filters/widget/:widgetId` API (Option A — the widget is the list).
 * Each list is a chip/tag input; a shared, session-only "Paste splits on"
 * radio controls how a pasted block is split into chips.
 */
export class FilterListManagement extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      words: [],
      handles: [],
      allowWords: [],
      splitMode: 'lines',
      loading: false,
      saving: false
    }

    this.handleListChange = this.handleListChange.bind(this)
    this.handleSplitModeChange = this.handleSplitModeChange.bind(this)
    this.handleSave = this.handleSave.bind(this)
    this.load = this.load.bind(this)
  }

  async componentDidMount () {
    if (this.props.widgetId) {
      await this.load(this.props.widgetId)
    }
  }

  async componentDidUpdate (previousProps) {
    if (
      previousProps.widgetId !== this.props.widgetId &&
      this.props.widgetId
    ) {
      await this.load(this.props.widgetId)
    }
  }

  async load (widgetId) {
    this.setState({ loading: true })
    try {
      const filters = await getWidgetFilters(widgetId)
      this.setState({
        words: toArray(filters.words),
        handles: toArray(filters.handles),
        allowWords: toArray(filters.allowWords),
        loading: false
      })
    } catch (error) {
      this.setState({ loading: false })
      window.toastr.error(`Error loading filter lists - ${error}`)
    }
  }

  handleListChange (key, nextValue) {
    this.setState({ [key]: nextValue })
  }

  handleSplitModeChange (event) {
    this.setState({ splitMode: event.target.value })
  }

  async handleSave () {
    const { widgetId } = this.props

    if (!widgetId) {
      window.toastr.error(
        'Please save the experience before editing filter lists.'
      )
      return
    }

    this.setState({ saving: true })
    try {
      await updateWidgetFilters(widgetId, {
        words: this.state.words,
        handles: this.state.handles,
        allowWords: this.state.allowWords
      })
      this.setState({ saving: false })
      window.toastr.success('Filter lists saved.')
    } catch (error) {
      this.setState({ saving: false })
      window.toastr.error(`Error saving filter lists - ${error}`)
    }
  }

  render () {
    const { loading, saving, splitMode } = this.state
    const { widgetId } = this.props
    // No widgetId on create flows (the experience must be saved first), and
    // editing is disabled while loading/saving so an in-flight load() can't
    // overwrite the user's edits.
    const disabled = loading || saving || !widgetId

    return (
      <div
        className='br-filter-list-management'
        style={{ marginBottom: '30px' }}
      >
        <h3>Filter List Management</h3>
        <span className='hidden-xs'>Filter out any inappropriate words</span>

        {!widgetId && (
          <p className='br-list-info'>
            Save the experience before editing filter lists.
          </p>
        )}

        {FIELDS.map(({ key, title, subTitle }) => (
          <ChipInput
            key={key}
            inputId={`br-filter-${key}`}
            label={title}
            subTitle={subTitle}
            tooltip={TOOLTIP}
            value={this.state[key]}
            splitMode={splitMode}
            disabled={disabled}
            onChange={next => this.handleListChange(key, next)}
          />
        ))}

        <div className='br-paste-split'>
          <span className='br-paste-split-label'>Paste splits on:</span>
          {SPLIT_MODES.map(mode => (
            <label className='br-paste-split-option' key={mode.value}>
              <input
                type='radio'
                name='br-paste-split'
                value={mode.value}
                checked={splitMode === mode.value}
                disabled={disabled}
                onChange={this.handleSplitModeChange}
              />
              {mode.label}
            </label>
          ))}
        </div>

        <button
          type='button'
          id='br-filter-save'
          className='btn green-haze'
          disabled={disabled}
          onClick={this.handleSave}
        >
          {saving ? 'Saving…' : 'Save filters'}
        </button>
      </div>
    )
  }
}

FilterListManagement.propTypes = {
  widgetId: PropTypes.string
}

export default FilterListManagement



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-filtering/filter-list-management.js